'use strict';

import { MeshStandardMaterial, Color, DoubleSide, Vector2, ShaderMaterial } from 'three';

const sea_pars_fragment = `
    uniform sampler2D uWave;
	uniform float uTime;
	uniform float uScale;
    uniform float uSpeed;
    uniform vec2 uFlow;
    uniform float uDistortion;
    uniform vec3 uSunDirection;
    uniform float uReflection;
    uniform float uReflectionPower;
    uniform vec3 uSunColor;
    
    uniform float sunriseTime;
    uniform float sunsetTime;
    uniform float currentTime;
    uniform float dayStartTime;
    uniform float dayEndTime;
`;

const sea_map_fragment = `
#ifdef USE_MAP
    vec2 seaUV = (vMapUv - 0.5) * uScale + 0.5;
    vec2 seaFlow = uTime * uSpeed * uFlow;
    seaUV += seaFlow;
    vec4 waterNoise = texture2D(uWave,seaUV);
    
    vec2 seaDisplacement = clamp((waterNoise.xy - 0.5) * uDistortion, -1., 1.);
    seaDisplacement += seaFlow * 5.0;
    
	vec4 sampledDiffuseColor = texture2D(map, vMapUv + seaDisplacement);
	float alpha = 0.0;
	float oneHour = 3600000.0;
	
	if (currentTime > sunsetTime - oneHour) {
        alpha = ((currentTime - (sunsetTime - oneHour)) / (sunsetTime - (sunsetTime - oneHour)));
    } else if (currentTime < sunriseTime + oneHour) {
        alpha = (sunriseTime + oneHour - currentTime) / (sunriseTime + oneHour - sunriseTime);
    }
    
    if (currentTime > sunsetTime) {
        alpha = 0.0;
    }
    
    if (currentTime < sunriseTime) {
        alpha = 0.0;
    }
    
    alpha = clamp(alpha,0.0,1.0);
	
	vec3 specularLight = vec3(0.0);
    vec3 refraction = uSunDirection-vSeaPosition.xyz;
    vec3 refractionDirection = normalize( refraction );
    sunLight( vSeaNormal, refractionDirection, uReflection, uReflectionPower, specularLight );
    specularLight = mix(vec3(0.0),specularLight,alpha);
    sampledDiffuseColor.rgb += specularLight;
	
	diffuseColor *= sampledDiffuseColor;

#endif
`;

const sea_pars_vertex = `
    varying vec3 vSeaNormal;
    varying vec4 vSeaPosition;
`;

const sea_world_pos = `
 vSeaPosition = modelMatrix * vec4( position, 1.0 );
`;

const sea_default_normal_vertex = `
    vSeaNormal = transformedNormal;
`;

const sea_reflection_fragment = `
void sunLight( const vec3 surfaceNormal, const vec3 refractionDirection, float shiny, float spec,inout vec3 specularColor ) {
    vec3 reflection = normalize( reflect( -uSunDirection, surfaceNormal ) );
    float direction = max( 0.0, dot( refractionDirection, reflection ) );
    specularColor += pow( direction, shiny ) * uSunColor * spec;
}
`;

export class SeaMaterial extends MeshStandardMaterial{
	#shader;
	
	constructor (directionalLight) {
		super();
		
		this.side = DoubleSide;
		this.customPrograms = [this.upgradeShader.bind(this)];
		this.noise = null;
		this.scale = 5;
		this.speed = 0.002;
		this.flow = new Vector2(-1,0.45);
		this.reflection = 80;
		this.reflectionPower = 1.5;
		this.sunColor = new Color('#FFA741');
		this.distortion = 0.08;
		this.dayInfo = {};
		this.currentTime = 0;
		this.light = directionalLight;
	}
	
	setDayTime(times) {
		this.dayInfo = times;
	}
	
	setTime(time) {
		this.currentTime = time;
	}
	
	tick(delta) {
		if (this.#shader) {
			this.#shader.uniforms.uTime.value += delta;
			this.#shader.uniforms.uSunDirection.value = this.light.position;
			this.#shader.uniforms.currentTime.value = this.currentTime;
			this.#shader.uniforms.sunriseTime.value = this.dayInfo.sunrise;
			this.#shader.uniforms.sunsetTime.value = this.dayInfo.sunset;
			this.#shader.uniforms.dayStartTime.value = this.dayInfo.dayStart;
			this.#shader.uniforms.dayEndTime.value = this.dayInfo.dayEnd;
		}
	}
	
	upgradeShader(shader) {
		shader.uniforms.uScale = {value: this.scale};
		shader.uniforms.uSpeed = {value: this.speed};
		shader.uniforms.uTime = {value: 0.0};
		shader.uniforms.uFlow = {value: this.flow};
		shader.uniforms.uDistortion = {value: this.distortion};
		shader.uniforms.uWave = {value: this.noise};
		shader.uniforms.uSunColor = {value: this.sunColor};
		shader.uniforms.uReflection = {value: this.reflection};
		shader.uniforms.uReflectionPower = {value: this.reflectionPower};
		shader.uniforms.uSunDirection = {value: this.light.position};
		shader.uniforms.sunriseTime = {value: this.dayInfo.sunrise};
		shader.uniforms.sunsetTime = {value: this.dayInfo.sunset};
		shader.uniforms.currentTime = {value: this.dayInfo.currentTime};
		shader.uniforms.dayStartTime = {value: this.dayInfo.dayStart};
		shader.uniforms.dayEndTime = {value: this.dayInfo.dayEnd};
		
		shader.vertexShader = sea_pars_vertex + shader.vertexShader;
		
		shader.vertexShader = shader.vertexShader.replace('#include <defaultnormal_vertex>',[
			'#include <defaultnormal_vertex>',
			sea_default_normal_vertex
		].join('\n'));
		
		shader.vertexShader = shader.vertexShader.replace('#include <worldpos_vertex>',[
			'#include <worldpos_vertex>',
			sea_world_pos
		].join('\n'));
		
		shader.fragmentShader = sea_pars_vertex + shader.fragmentShader;
		shader.fragmentShader = sea_pars_fragment + shader.fragmentShader;
		
		shader.fragmentShader = shader.fragmentShader.replace('#include <clipping_planes_pars_fragment>',[
			'#include <clipping_planes_pars_fragment>',
			sea_reflection_fragment
		].join('\n'));
		
		shader.fragmentShader = shader.fragmentShader.replace('#include <map_fragment>',[
			sea_map_fragment
		].join('\n'));
		
		this.#shader = shader;
	}
	
}
